// =============================================================================
// src/features/briefing/briefing-markup.ts — Markup do "Briefing do dia".
//
// Proveniencia: criado na reforma institucional 2026-06 (D2): entrada
//   command-center no padrao visual dos takeovers (Observatory claro).
//   REFORMADO em 2026-07-28 (pedido do usuario "dados reais, sem alertas"):
//   a lista "Alertas ativos" saiu por completo; a faixa 2x2 exibe as 4 analises
//   do social_listening (briefing-data); o eyebrow usa a data REAL (injetada,
//   default new Date() — unico ponto de relogio do briefing) e o rodape de
//   proveniencia nomeia a origem resolvida em runtime (analyticsLiveSource).
//   Estilos em src/styles/briefing.css.
// =============================================================================

import {
  BRIEFING_ELECTION_YEAR,
  briefingDateLabel,
  buildBriefingHeadline,
  buildBriefingKpis,
  buildBriefingMapLegend,
  type BriefingKpi,
  type BriefingTrendDir,
} from './briefing-data';
import { analyticsLiveSource, getAnalyticsFreshness } from '../analytics/analytics-live-store';
import { analyticsProvenanceUpdateLabel } from '../../data/analytics-overlay-scope';
import { buildBriefingProvenanceFooterMarkup } from '../../data/data-provenance';

/** Icone Tabler de tendencia por direcao (sem setas unicode — reforma D1). */
const TREND_ICON_BY_DIR: Record<BriefingTrendDir, string> = {
  up: 'ti-trending-up',
  down: 'ti-trending-down',
  flat: 'ti-minus',
};

/** HTML de um KPI da faixa (label + valor + tendencia + rodape). */
function briefingKpiHtml(kpi: BriefingKpi): string {
  const valueClass = kpi.valueKind === 'text' ? 'u-brief-kpi-value is-text' : 'u-brief-kpi-value';
  return `<div class="u-brief-kpi">
    <div class="u-brief-kpi-label">${kpi.label}</div>
    <div class="u-brief-kpi-row">
      <span class="${valueClass}">${kpi.value}</span>
      <span class="u-brief-kpi-trend is-${kpi.trendDir}"><i class="ti ${TREND_ICON_BY_DIR[kpi.trendDir]}" aria-hidden="true"></i> ${kpi.trendText}</span>
    </div>
    <div class="u-brief-kpi-foot">${kpi.foot}</div>
  </div>`;
}

/** HTML da legenda do mini-mapa (top partidos com cor de dado + contagem). */
function briefingMapLegendHtml(): string {
  const chips = buildBriefingMapLegend()
    .map(({ party, color, count }) =>
      `<span class="u-brief-legend-chip"><span class="u-brief-legend-dot" style="background:${color}"></span>${party} · ${count}</span>`)
    .join('');
  return `<div class="u-brief-map-legend">${chips}</div>`;
}

/** Cabecalho: eyebrow com a data REAL + manchete-sintese do social_listening. */
function briefingHeadHtml(reference: Date): string {
  return `<header class="u-brief-head">
    <div>
      <div class="u-brief-eyebrow">Briefing do dia <span class="u-brief-eyebrow-sep"></span> ${briefingDateLabel(reference)} <span class="u-brief-eyebrow-sep"></span> Manaus e interior</div>
      <h1 class="u-brief-headline">${buildBriefingHeadline()}</h1>
    </div>
  </header>`;
}

/**
 * Coluna lateral: faixa 2×2 de KPIs do social_listening + CTAs. (A lista de
 * "Alertas ativos" foi REMOVIDA na reforma 2026-07-28, pedido do usuario.)
 *
 * O CTA #briefingCopilotCta nasce `disabled`: o Copiloto IA foi desativado em
 * 2026-07-21 e, sem o atributo, o clique cairia no FALLBACK de app/commands.ts,
 * que mostra um toast — pior do que inerte, porque fingiria ter feito algo.
 * Ver initFeatures() em main.ts.
 */
function briefingSideColumnHtml(): string {
  return `<div class="u-brief-side">
    <div class="u-brief-kpis">${buildBriefingKpis().map(briefingKpiHtml).join('')}</div>
    <div class="u-brief-actions">
      <button type="button" class="u-cta-btn" id="briefingExploreCta"><i class="ti ti-map-2" aria-hidden="true"></i> Explorar mapa completo</button>
      <button type="button" class="u-cta-btn secondary" id="briefingCopilotCta" disabled aria-disabled="true" title="Copiloto IA indisponível"><i class="ti ti-message-circle" aria-hidden="true"></i> Perguntar ao Copiloto</button>
    </div>
  </div>`;
}

/** Modulo central: mini-mapa do AM pintado por partido + rodape clicavel. */
function briefingMapCardHtml(): string {
  return `<div class="u-brief-map-card" id="briefingMapCard" role="button" tabindex="0"
       aria-label="Abrir o mapa Explorar do Amazonas">
    <div id="briefingMap" aria-hidden="true"></div>
    <div class="u-brief-map-foot">
      <span class="u-brief-map-caption">Partido mais votado por município · ${BRIEFING_ELECTION_YEAR} · Prefeito</span>
      ${briefingMapLegendHtml()}
      <span class="u-brief-map-hint"><i class="ti ti-arrow-right" aria-hidden="true"></i> Abrir Explorar</span>
    </div>
  </div>`;
}

/**
 * Markup completo da rota fullscreen do Briefing (montada em #routeLayer).
 * `reference` e o relogio do render (default: agora) — main.ts chama sem args
 * DEPOIS da hidratacao do Analytics, entao os getters ja devolvem o dado vivo.
 *
 * @example
 *   mountPoint('routeLayer').innerHTML = buildBriefingPageMarkup() + ...;
 */
export function buildBriefingPageMarkup(reference: Date = new Date()): string {
  return `<div id="u-briefing-page" class="u-briefing-page" aria-hidden="true">
  <div class="u-brief-shell">
    ${briefingHeadHtml(reference)}
    <div class="u-brief-grid">
      ${briefingSideColumnHtml()}
      ${briefingMapCardHtml()}
    </div>
    <div class="u-brief-provenance" id="briefingProvenance">${buildBriefingProvenanceFooterMarkup(analyticsLiveSource(), analyticsProvenanceUpdateLabel(getAnalyticsFreshness()))}</div>
  </div>
</div>`;
}
